import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { fetchSupportedLanguages, requestTranslation } from './translationApi'
import { consultTranslationAvailability } from './useTranslationAvailability'
import { normalizeLocaleToServiceCode } from './localeNormalization'

/**
 * RN10/CA8: MIC's own safeguard against the LLM's token ceiling and the backend timeout.
 * The service itself imposes no limit.
 */
export const MAX_TEXT_LENGTH = 5000

/**
 * Free-text translator state machine (story #119 logic, extracted from the dialog for #2892).
 * UI-less: the Provider hosts it so that minimizing the widget or changing route never loses an
 * in-flight translation (RN3.1).
 *
 * @param {object} params
 * @param {boolean} params.active load health + catalogue while true (closed → open edge)
 * @param {() => void} [params.onSettled] called after a translation succeeds or fails — never after cancel
 */
const useTranslator = ({ active, onSettled }) => {
  const { i18n } = useSmartTranslation()
  const [languages, setLanguages] = useState([])
  // Two independent answers arrive on activation (health and catalogue), so they get one flag
  // each: a healthy-looking catalogue must never overwrite a KO health answer, or vice versa.
  const [healthKo, setHealthKo] = useState(false)
  const [catalogueKo, setCatalogueKo] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [translation, setTranslation] = useState('')
  const [translationFailed, setTranslationFailed] = useState(false)
  const [text, setText] = useState('')
  const [sourceLanguage, setSourceLanguage] = useState('')
  const [targetLanguage, setTargetLanguage] = useState('')
  const inFlight = useRef(null)
  // Latest callback without re-creating `translate` on every Provider render.
  const settled = useRef(onSettled)
  settled.current = onSettled

  useEffect(() => {
    if (!active) return

    let alive = true
    setHealthKo(false)
    setCatalogueKo(false)
    // GATE 1 nº2: health is re-consulted on activation, through the shared ~60 s cache.
    consultTranslationAvailability()
      .then((serviceAvailable) => { if (alive) setHealthKo(!serviceAvailable) })

    fetchSupportedLanguages()
      .then((catalogue) => {
        if (!alive) return
        setLanguages(catalogue)
        setCatalogueKo(catalogue.length === 0)
        // RN9 + RN9.1: preselect the target with the user's own language, but only when the
        // service supports it — judged on the normalised code.
        const userLanguage = normalizeLocaleToServiceCode(i18n.language)
        const supported = catalogue.some((language) => language.code === userLanguage)
        setTargetLanguage(supported ? userLanguage : '')
      })
      .catch(() => {
        // D-N1/E8: without a catalogue there is nothing to offer — counts as unavailable.
        if (!alive) return
        setLanguages([])
        setCatalogueKo(true)
      })

    return () => { alive = false }
  }, [active, i18n.language])

  // D-N2: cancelling aborts the request and leaves nothing behind — no partial result, no error.
  const cancel = useCallback(() => {
    if (!inFlight.current) return
    inFlight.current.abort()
    inFlight.current = null
    setTranslating(false)
  }, [])

  // RN3: closing wipes everything — text, result and the chosen pair.
  const reset = useCallback(() => {
    cancel()
    setText('')
    setTranslation('')
    setTranslationFailed(false)
    setSourceLanguage('')
    setTargetLanguage('')
  }, [cancel])

  const translate = useCallback(() => {
    // A second call while one is in flight is ignored; the UI disables the button, this is the hook's own safeguard.
    if (inFlight.current) return
    const controller = new AbortController()
    inFlight.current = controller
    setTranslating(true)
    setTranslation('')
    setTranslationFailed(false)

    requestTranslation({ text, sourceLanguage, targetLanguage }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setTranslation(result.translatedText)
      })
      // RN6/RN14: every failure collapses into the same generic notice.
      .catch(() => {
        if (controller.signal.aborted) return
        setTranslationFailed(true)
      })
      .finally(() => {
        if (controller.signal.aborted) return
        inFlight.current = null
        setTranslating(false)
        if (settled.current) settled.current()
      })
  }, [text, sourceLanguage, targetLanguage])

  // RN1/E7/E8: no health and no catalogue mean the same to the user — unusable right now.
  const unavailable = healthKo || catalogueKo
  const overLimit = text.length > MAX_TEXT_LENGTH
  // RN11/V1: whitespace is not text. V5: both languages must be chosen.
  const canTranslate = !unavailable && !overLimit &&
    sourceLanguage !== '' && targetLanguage !== '' && text.trim() !== ''

  // The Provider memoizes its context value on this object, so its identity must only change
  // when its content does — otherwise every Provider render would defeat that memoization.
  return useMemo(() => ({
    languages,
    sourceLanguage,
    targetLanguage,
    text,
    translating,
    translation,
    translationFailed,
    healthKo,
    catalogueKo,
    unavailable,
    canTranslate,
    overLimit,
    setText,
    setSourceLanguage,
    setTargetLanguage,
    translate,
    cancel,
    reset
  }), [languages, sourceLanguage, targetLanguage, text, translating, translation, translationFailed,
    healthKo, catalogueKo, unavailable, canTranslate, overLimit, translate, cancel, reset])
}

export default useTranslator
